import { useNavigate } from '@tanstack/react-router';
import { usePlannedWorkouts, useWorkouts } from '../hooks/useProfile';
import { dayjs, hasDisplayValue, parsePlannedDate } from '../lib/utils';
import { Check, Info } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import { useState, useRef, useEffect } from 'react';
import WorkoutCard from './WorkoutCard';
import { formatPlannedWorkoutMinutes } from '../lib/plannedWorkout';

const numericValuePattern = /^\d+(?:\.\d+)?$/;
const hasUnitPattern = /[a-zA-Z/]/;

const formatPlannedDistance = (distance: unknown) => {
  const value = String(distance).trim();
  if (!numericValuePattern.test(value) || hasUnitPattern.test(value)) {
    return value;
  }
  return `${value} mi`;
};

type TodaysWorkoutsProps = {
  selectedDate?: Dayjs;
  onSelectDate?: (date: Dayjs | ((date: Dayjs) => Dayjs)) => void;
};

const TodaysWorkouts = ({
  selectedDate: controlledSelectedDate,
  onSelectDate,
}: TodaysWorkoutsProps) => {
  const navigate = useNavigate();
  const [localSelectedDate, setLocalSelectedDate] = useState(dayjs());
  const selectedDate = controlledSelectedDate || localSelectedDate;
  const setSelectedDate = onSelectDate || setLocalSelectedDate;
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: plannedWorkoutsData, isLoading } = usePlannedWorkouts({
    startDate: dayjs().subtract(90, 'day').toDate(),
  });
  const plannedWorkouts = plannedWorkoutsData || [];

  const { data: workoutData } = useWorkouts();
  const completedWorkouts = workoutData?.workouts || [];

  const selectedDateLabel = selectedDate.isSame(dayjs(), 'day')
    ? 'Today'
    : selectedDate.format('dddd');
  const selectedDateWorkouts = (plannedWorkouts || []).filter((w: any) => {
    const wDate = parsePlannedDate(w.planned_date).format('YYYY-MM-DD');
    return wDate === selectedDate.format('YYYY-MM-DD');
  });
  const linkedWorkoutIds = new Set(
    plannedWorkouts
      .map((workout) => workout.activity_id)
      .filter((activityId) => activityId != null)
      .map(String),
  );
  const unlinkedCompletedWorkouts = completedWorkouts.filter(
    (workout) =>
      dayjs(workout.completedAt).isSame(selectedDate, 'day') &&
      !linkedWorkoutIds.has(String(workout.id)),
  );
  const hasNoWorkouts =
    selectedDateWorkouts.length === 0 && unlinkedCompletedWorkouts.length === 0;
  const showEmptyState = !isLoading && hasNoWorkouts;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let startX = 0;
    let startY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const diffX = endX - startX;
      const diffY = endY - startY;

      if (Math.abs(diffX) > 50 && Math.abs(diffX) > Math.abs(diffY)) {
        if (diffX > 0) {
          setSelectedDate((d) => d.subtract(1, 'day'));
        } else {
          setSelectedDate((d) => d.add(1, 'day'));
        }
      }
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchend', handleTouchEnd);
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  return (
    <div ref={containerRef} className="select-none">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold tracking-tight text-brand-midnight dark:text-white">
          {selectedDateLabel}
        </h2>
        {showEmptyState ? (
          <button
            type="button"
            onClick={() => navigate({ to: '/plan/workout/new' })}
            className="button-secondary px-4 py-2 text-xs"
          >
            + Create workout
          </button>
        ) : null}
      </div>
      {isLoading ? (
        <div className="animate-pulse bg-gray-100 rounded-xl h-32" />
      ) : hasNoWorkouts ? (
        <div className="surface-card p-7 text-center">
          <p className="text-sm font-semibold text-brand-midnight dark:text-white">
            No workout planned
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Take the recovery day, or add a session to keep your week moving.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {selectedDateWorkouts.map((workout: any) => {
            const linkedWorkout = workout.activity_id
              ? completedWorkouts.find((w: any) => w.id === workout.activity_id)
              : null;

            return (
              <div
                key={workout.id}
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigate({
                    to: '/plan/workout/$id',
                    params: { id: workout.id },
                  })
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    navigate({
                      to: '/plan/workout/$id',
                      params: { id: workout.id },
                    });
                  }
                }}
                className="depth-panel block w-full cursor-pointer rounded-2xl p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-xl"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900 capitalize">
                    {workout.title || workout.type}
                  </h3>
                  {workout.completed ? (
                    <span className="flex items-center gap-1 rounded-full bg-brand-volt px-2.5 py-1 text-xs font-bold text-brand-midnight ring-1 ring-brand-midnight/10">
                      <Check className="h-3 w-3" />
                      Done
                    </span>
                  ) : (
                    <span className="rounded-full bg-brand-cobalt px-2.5 py-1 text-xs font-bold text-white ring-1 ring-brand-cobalt/20">
                      Scheduled
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs font-medium text-gray-600">
                      Distance
                    </div>
                    <div className="font-semibold text-gray-900">
                      {hasDisplayValue(workout.distance)
                        ? formatPlannedDistance(workout.distance)
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-600">
                      Est Time
                    </div>
                    <div className="font-semibold text-gray-900">
                      {hasDisplayValue(workout.total_time)
                        ? formatPlannedWorkoutMinutes(workout.total_time)
                        : '—'}
                    </div>
                  </div>
                </div>

                {hasDisplayValue(workout.summary) ||
                hasDisplayValue(workout.details) ? (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                      <Info className="h-3 w-3" />
                      Description
                    </div>
                    {hasDisplayValue(workout.summary) ? (
                      <p className="text-sm font-medium text-gray-800 whitespace-pre-wrap">
                        {workout.summary}
                      </p>
                    ) : null}
                    {hasDisplayValue(workout.details) ? (
                      <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                        {workout.details}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {linkedWorkout ? (
                  <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
                    <span className="text-emerald-700 dark:text-brand-volt">
                      Linked: {linkedWorkout.title || 'Completed workout'}
                    </span>
                  </div>
                ) : null}
              </div>
            );
          })}
          {unlinkedCompletedWorkouts.map((workout) => (
            <WorkoutCard
              key={workout.id}
              workout={workout}
              onClick={() =>
                navigate({
                  to: '/workouts/$id',
                  params: { id: workout.id.toString() },
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TodaysWorkouts;
